import { For, Show, createResource } from "solid-js";

import AsyncResource from "../components/AsyncResource";
import PageHeader from "../components/PageHeader";
import {
  api,
  type DeploymentUnitStatus,
  type EnvironmentHealthCheck,
  type EnvironmentHealthStatus,
} from "../lib/api";

const STATUS_LABELS: Record<EnvironmentHealthStatus, string> = {
  pass: "Pass",
  warn: "Warn",
  fail: "Fail",
  not_configured: "Not configured",
};

export default function EnvironmentHealth() {
  const [report, { refetch }] = createResource(api.getEnvironmentHealth);

  return (
    <>
      <PageHeader
        title="Environment"
        subtitle="Public edge health"
        actions={
          <button
            type="button"
            class="btn-secondary"
            disabled={report.loading}
            onClick={() => refetch()}
          >
            Refresh
          </button>
        }
      />

      <AsyncResource resource={report}>
        {(data) => (
          <div class="space-y-4">
            <div class="card">
              <div class="card-body">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <SummaryItem label="Environment" value={data().environment} />
                  <SummaryItem
                    label="Checked"
                    value={formatTimestamp(data().checked_at)}
                  />
                  <SummaryItem
                    label="Deploy Units"
                    value={deploymentSummary(data().deployment_units)}
                  />
                  <div>
                    <div class="text-xs font-medium text-slate-500 mb-1">
                      Overall
                    </div>
                    <span class={statusClass(data().overall_status)}>
                      {STATUS_LABELS[data().overall_status]}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                <h2 class="text-sm font-semibold">Deployment Units</h2>
              </div>
              <div class="overflow-x-auto">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Status</th>
                      <th>Commit</th>
                      <th>Live Images</th>
                      <th>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data().deployment_units}>
                      {(unit) => <DeploymentUnitRow unit={unit} />}
                    </For>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                <h2 class="text-sm font-semibold">Checks</h2>
              </div>
              <div class="overflow-x-auto">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Check</th>
                      <th>Status</th>
                      <th>Message</th>
                      <th>Public URL</th>
                      <th>Expected</th>
                      <th>Observed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={data().checks}>
                      {(check) => <HealthCheckRow check={check} />}
                    </For>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </AsyncResource>
    </>
  );
}

function SummaryItem(props: { label: string; value: string }) {
  return (
    <div>
      <div class="text-xs font-medium text-slate-500 mb-1">{props.label}</div>
      <div class="font-mono text-xs text-slate-800 break-all">{props.value}</div>
    </div>
  );
}

function DeploymentUnitRow(props: { unit: DeploymentUnitStatus }) {
  const unit = () => props.unit;
  return (
    <tr>
      <td class="max-w-xs">
        <div class="font-medium font-mono">{unit().key}</div>
        <div class="text-xs text-slate-500">{unit().mode}</div>
      </td>
      <td>
        <span class={statusClass(unit().status)}>
          {STATUS_LABELS[unit().status]}
        </span>
        <div class="text-xs text-slate-500 mt-1">{unit().message}</div>
      </td>
      <td class="max-w-xs">
        <HealthValue value={formatCommit(unit().source_commit)} />
        <div class="text-xs text-slate-500">{unit().source_ref}</div>
      </td>
      <td class="max-w-sm">
        <span class="font-mono text-xs text-slate-700 break-all">
          {liveImageSummary(unit())}
        </span>
      </td>
      <td class="max-w-xs">
        <HealthValue value={formatTimestamp(unit().verified_at)} />
      </td>
    </tr>
  );
}

function HealthCheckRow(props: { check: EnvironmentHealthCheck }) {
  const check = () => props.check;
  return (
    <tr>
      <td>
        <div class="font-medium">{check().target}</div>
        <div class="text-xs text-slate-500 font-mono">{check().id}</div>
        <div class="text-xs text-slate-500">{check().category}</div>
      </td>
      <td>
        <span class={statusClass(check().status)}>
          {STATUS_LABELS[check().status]}
        </span>
      </td>
      <td class="max-w-sm">
        <div class="text-sm text-slate-700">{check().message}</div>
      </td>
      <td class="max-w-xs">
        <Show when={check().public_url} fallback={<span class="text-slate-400">-</span>}>
          {(url) => (
            <a
              class="font-mono text-xs text-blue-700 hover:underline break-all"
              href={url()}
              target="_blank"
              rel="noreferrer"
            >
              {url()}
            </a>
          )}
        </Show>
      </td>
      <td class="max-w-xs">
        <HealthValue value={check().expected} />
      </td>
      <td class="max-w-xs">
        <HealthValue value={check().observed} />
      </td>
    </tr>
  );
}

function HealthValue(props: { value?: string | null }) {
  return (
    <Show when={props.value} fallback={<span class="text-slate-400">-</span>}>
      {(value) => (
        <span class="font-mono text-xs text-slate-700 break-all">{value()}</span>
      )}
    </Show>
  );
}

function statusClass(status: EnvironmentHealthStatus) {
  switch (status) {
    case "pass":
      return "badge bg-emerald-100 text-emerald-800";
    case "warn":
      return "badge bg-amber-100 text-amber-800";
    case "fail":
      return "badge bg-red-100 text-red-800";
    case "not_configured":
      return "badge bg-slate-200 text-slate-700";
  }
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function formatCommit(value?: string | null) {
  if (!value) return undefined;
  return value.length > 12 ? value.slice(0, 12) : value;
}

function liveImageSummary(unit: DeploymentUnitStatus) {
  const tags = new Set<string>();
  for (const image of unit.images) {
    for (const service of image.live_services) {
      if (service.image_tag) tags.add(service.image_tag.slice(0, 12));
    }
  }
  if (tags.size === 0) return "-";
  return [...tags].sort().join(", ");
}

function deploymentSummary(units: DeploymentUnitStatus[]) {
  if (units.length === 0) return "0";
  const failed = units.filter((unit) => unit.status === "fail").length;
  const warned = units.filter((unit) => unit.status === "warn").length;
  if (failed > 0) return `${units.length} total, ${failed} fail`;
  if (warned > 0) return `${units.length} total, ${warned} warn`;
  return `${units.length} total`;
}
